import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} QuestItem
 * @property {number} quest_id
 * @property {string} quest_name
 * @property {string} episodes
 * @property {number} percentage
 * @property {string | null} due_date
 * @property {string} status
 */

/**
 * @typedef {object} StudentInfo
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {string} username
 * @property {string} age
 * @property {string} student_code
 * @property {number} overall_progress
 * @property {number} avg_quiz_score
 * @property {number} total_quests_assigned
 */

/**
 * @typedef {object} StudentReportResponse
 * @property {StudentInfo} student
 * @property {Object<string, QuestItem[]>} quests_by_category
 */

/**
 * @typedef {object} GetStudentReportParams
 * @property {number | string} studentId - Student ID (required)
 */

/**
 * Custom hook to fetch the student report by ID for teacher
 */
export const useGetStudentReportById = () => {
  const [report, setReport] = useState(
    /** @type {StudentReportResponse | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch individual student report by ID
   * @async
   * @param {GetStudentReportParams} params
   */
  const getStudentReportById = async ({ studentId } = {}) => {
    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/teacher/reports/students/${studentId}`;

      const response = await axiosInstance.get(url);

      setReport(response?.data || null);
    } catch (err) {
      setReport(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    report,
    loading,
    error,
    getStudentReportById,
    setReport,
  };
};
